import {
    ActionIcon,
    Badge,
    Card,
    Checkbox,
    Group,
    Popover,
    Select,
    Stack,
    Table,
    Text,
    TextInput,
    UnstyledButton,
} from '@mantine/core';
import { useNavigate } from 'react-router';

import { brand } from '../../branding';
import { columnValue, compareText, type CategoryLabels, type SortState } from '../engine';
import { useCatalogLabels } from '../labels';
import { ProductImage } from './ProductImage';
import { WishlistButton } from './WishlistButton';

import type { IndexItem, ListColumn } from '../types';

/** List-view-only "select for compare" column (see WishlistPage) - an extra leading checkbox
 * per row, capped at `max` simultaneous selections (further checkboxes disable once reached,
 * rather than silently dropping the oldest one). */
export interface ProductGridSelection {
    selectedArtNrs: Set<string>;
    onToggle: (artNr: string) => void;
    max: number;
}

interface ProductGridProps {
    items: IndexItem[];
    viewMode: 'grid' | 'list';
    columns?: ListColumn[];
    /** Current sort - only Name/Artikelnummer/Kategorie are click-to-sort (see ColumnHeader);
     * omitted (e.g. WishlistPage's own product section) hides the sort affordance entirely. */
    sort?: SortState;
    onSort?: (key: string) => void;
    columnFilters?: Record<string, string>;
    onColumnFilter?: (key: string, value: string) => void;
    /** Breadcrumb label per artNr, resolved from the tree on demand (see engine.buildCategoryLabels). */
    categoryLabels?: CategoryLabels;
    /** Distinct Kategorie values (see engine.distinctColumnValues) - turns that column's filter
     * into a dropdown of the values actually present instead of a free-text box the user has to
     * type exactly right. Omitted (or empty) keeps the free-text fallback. */
    categoryOptions?: string[];
    /**
     * How much of an item's tree path to show (list column and grid card alike):
     * - `'path'`: the full breadcrumb, "Systeme › Decken-Systeme › Deckentische › DokaXdek-Tisch",
     *   under the heading "Kategorie" - what a FREE-TEXT SEARCH needs, since a hit can come from
     *   anywhere in the tree and the path is the only thing telling the user where.
     * - `'leaf'`: just the last level, "DokaXdek-Tisch", under the heading "System" - what BROWSING
     *   the category tree needs: the upper levels are already in the breadcrumb and the tree
     *   selection above, so repeating them per row only pushed the informative part off to the
     *   right (see chat + screenshot: "wenn wir über den Kategoriebaum kommen zeigen wir nur die
     *   letzte Ebene des Pfades (Headline 'System')").
     * - `'none'`: no such column (and no category line on the grid card) at all - for a list that
     *   is ALREADY one system's own articles (SystemDetailContent's Artikelliste), where every row
     *   would repeat the system named in the page header right above it (see chat: "wenn wir in
     *   einem System die Artikel anzeigen, lassen wir die Spalte 'System' weg").
     * Defaults to `'path'`, the previous behaviour, for callers with no notion of searching
     * (WishlistPage).
     */
    categoryDisplay?: 'path' | 'leaf' | 'none';
    /** Only honored in list view - grid tiles never show the compare checkbox. */
    selection?: ProductGridSelection;
}

// RELATIVE on purpose: React Router resolves it against the route this grid is rendered under, so
// the same link opens the article overlay over the catalog (`/catalog/article/…`) or over the
// Merkliste (`/catalog/wishlist/article/…`) - whichever the user came from stays behind it (see
// components/DetailOverlay.tsx). An absolute path would always land in the catalog and lose that
// context.
const openHref = (artNr: string) => `article/${encodeURIComponent(artNr)}`;

// Module-scope so its identity is stable across renders (otherwise the input remounts and
// loses focus on every keystroke).
const FilterCell = ({
    value,
    placeholder,
    options,
    onChange,
}: {
    value: string;
    placeholder: string;
    /** When given, the filter is a dropdown of these exact values rather than free text. The
     * stored filter value stays a plain string either way, so engine.applyColumnFilters' own
     * "contains" matching needs no special case - an exact value trivially contains itself. */
    options?: string[];
    onChange: (value: string) => void;
}) =>
    options && options.length > 0 ? (
        <Select
            // withinPortal:false keeps this dropdown inside the enclosing filter Popover's own
            // DOM subtree. Portaled (the default) it renders as a sibling of that popover, so
            // clicking an option counts as a click OUTSIDE it and closes the whole filter popover
            // before the selection registers.
            comboboxProps={{ withinPortal: false }}
            data={options}
            placeholder={placeholder}
            size="xs"
            value={value || null}
            clearable
            searchable
            onChange={(next) => onChange(next ?? '')}
            onClick={(event) => event.stopPropagation()}
        />
    ) : (
        <TextInput
            placeholder={placeholder}
            size="xs"
            value={value}
            onChange={(event) => onChange(event.currentTarget.value)}
            onClick={(event) => event.stopPropagation()}
        />
    );

// A column header that's optionally click-to-sort (label itself is the trigger, with an arrow
// showing the active direction) and/or carries its own Excel-style filter popover (funnel icon in
// front of the title, filled once a filter value is set) - replaces the old single "toggle a filter row for every
// column" affordance with a per-column one, and the separate sort Select in Toolbar with sorting
// driven directly from the column that's actually being sorted.
const ColumnHeader = ({
    label,
    sortKey,
    sort,
    onSort,
    filterKey,
    filterValue,
    filterOptions,
    onFilterChange,
    filterPlaceholder,
}: {
    label: string;
    sortKey?: string;
    sort?: SortState;
    onSort?: (key: string) => void;
    filterKey?: string;
    filterValue?: string;
    filterOptions?: string[];
    onFilterChange?: (key: string, value: string) => void;
    filterPlaceholder?: string;
}) => {
    const isSorted = Boolean(sortKey) && sort?.key === sortKey;
    // Distinguishes each column's filter button for assistive tech - every column previously
    // shared the exact same generic aria-label/title ("contains...", filterPlaceholder alone).
    const filterAriaLabel = filterPlaceholder ? `${label} - ${filterPlaceholder}` : undefined;

    return (
        // Funnel FIRST, then the title (reported: "Das Filter icon in der tabellarischen Darstellung
        // soll vor dem Spaltentitel kommen"). Pushed to the far right edge it sat right next to the
        // NEXT column's title and read as belonging to that one.
        <Group gap={4} wrap="nowrap">
            {filterKey && onFilterChange && (
                <Popover position="bottom-start" shadow="md" withArrow>
                    <Popover.Target>
                        {/* Dark and filled = a filter is set, light grey = none (reported: "Dunkler
                            Filter > Filter gesetzt, heller Filter > Filter nicht gesetzt"). The
                            unset state used to be the brand blue and read as "active" itself.
                            Colour set inline: it has to beat @uag/doka-design's ActionIcon rules. */}
                        <ActionIcon
                            aria-label={filterAriaLabel}
                            aria-pressed={Boolean(filterValue)}
                            color={filterValue ? undefined : 'gray'}
                            // The glyph, not the button's box, starts on the column's content
                            // edge - with the cells' equal padding (TABLE_STYLES) the funnel sits
                            // straight above the values instead of a few px to their right.
                            ml={-3}
                            size="sm"
                            style={filterValue ? undefined : { color: 'var(--mantine-color-gray-5)' }}
                            title={filterAriaLabel}
                            variant={filterValue ? 'filled' : 'subtle'}
                            onClick={(event) => event.stopPropagation()}
                        >
                            <span className="material-symbols-outlined" style={{ fontSize: 16 }} aria-hidden>
                                filter_alt
                            </span>
                        </ActionIcon>
                    </Popover.Target>
                    <Popover.Dropdown onClick={(event) => event.stopPropagation()}>
                        <FilterCell
                            options={filterOptions}
                            placeholder={filterPlaceholder ?? ''}
                            value={filterValue ?? ''}
                            onChange={(value) => onFilterChange(filterKey, value)}
                        />
                    </Popover.Dropdown>
                </Popover>
            )}
            {sortKey && onSort ? (
                <UnstyledButton
                    className="flex items-center gap-1"
                    onClick={(event) => {
                        event.stopPropagation();
                        onSort(sortKey);
                    }}
                >
                    <Text fw={600} size="sm">
                        {label}
                    </Text>
                    {isSorted && (
                        <span className="material-symbols-outlined" style={{ fontSize: 16 }} aria-hidden>
                            {sort?.dir === 'desc' ? 'arrow_downward' : 'arrow_upward'}
                        </span>
                    )}
                </UnstyledButton>
            ) : (
                <Text fw={600} size="sm">
                    {label}
                </Text>
            )}
        </Group>
    );
};

/** Small, quiet labels in the app's colours - grey frame and text for the type, the dark blue for
 * "lokal" (the one a user has to notice); the former yellow/orange fills broke the colour system
 * (reported: "Gehe nochmal über alle Farben im Frontend"). */
const labelBadgeStyles = (accent: boolean) => ({
    root: {
        backgroundColor: '#fff',
        border: `1px solid ${accent ? 'var(--pih-primary)' : 'var(--pih-border)'}`,
        borderRadius: 4,
        color: accent ? 'var(--pih-primary)' : 'var(--pih-muted)',
        fontWeight: 600,
        textTransform: 'none' as const
    }
});

// Only the exceptions (part/family) get a badge - same "common case stays badge-free"
// principle as ScopeBadge below.
const TypeBadge = ({ item }: { item: IndexItem }) => {
    const { L } = useCatalogLabels();
    if (item.type === 'part' || item.family) {
        return (
            <Badge styles={labelBadgeStyles(false)} variant="outline">
                {item.type === 'part' ? L.part : L.family}
            </Badge>
        );
    }
    return null;
};

// Only the "local" exception gets a badge - "global" is the common case and stays badge-free so
// the grid/list doesn't get noisier for the majority of articles.
const ScopeBadge = ({ item }: { item: IndexItem }) => {
    const { L } = useCatalogLabels();
    if (item.scope !== 'local') {
        return null;
    }
    return (
        <Badge styles={labelBadgeStyles(true)} variant="outline">
            {L.scopeLocal}
        </Badge>
    );
};

/**
 * The same horizontal padding for header and body cells, inline so @uag/doka-design's cell rules
 * can't differ between the two - they did, and the header's funnels sat visibly right of the
 * column's values (reported: "Die Filtersymbole in der Tabelle sind leicht nach rechts verrutscht").
 * Grey rules between the rows, a grey stripe and hover - no blue tint.
 */
const TABLE_STYLES = {
    th: { paddingLeft: 12, paddingRight: 12, color: 'var(--pih-text)', borderBottom: '1px solid var(--pih-border)' },
    td: { paddingLeft: 12, paddingRight: 12, color: 'var(--pih-text)' },
    tr: { borderColor: 'var(--pih-border)' }
} as const;

/** Last level of a "A › B › C" breadcrumb label - the system itself (see categoryDisplay). */
const leafOf = (label: string): string => label.split('›').pop()?.trim() ?? label;

export const ProductGrid = ({
    items,
    viewMode,
    columns = [],
    sort,
    onSort,
    columnFilters = {},
    onColumnFilter,
    categoryLabels,
    categoryOptions,
    categoryDisplay = 'path',
    selection,
}: ProductGridProps) => {
    const navigate = useNavigate();
    const { L } = useCatalogLabels();

    const showLeafOnly = categoryDisplay === 'leaf';
    const showCategory = categoryDisplay !== 'none';
    const categoryText = (artNr: string): string => {
        const label = categoryLabels?.get(artNr);
        if (!label) {
            return '';
        }
        return showLeafOnly ? leafOf(label) : label;
    };
    // The filter itself keeps matching against the FULL path (engine.applyColumnFilters does a
    // "contains" on it), so offering the leaves alone as options is enough - a leaf trivially
    // occurs in the path it belongs to. Deduplicated: the same system name can sit under more
    // than one parent, and two identical entries in a dropdown are just confusing.
    const filterOptions = showLeafOnly
        ? [...new Set((categoryOptions ?? []).map(leafOf))].sort(compareText)
        : categoryOptions;

    if (viewMode === 'list') {
        return (
            // Scrolls horizontally below minWidth instead of the table forcing the whole page
            // wider than the viewport - this many columns (image, name, artNr, category, extra
            // `columns`, badges/wishlist) never fit a mobile screen, and unlike the nowrap-Group
            // title bugs above, a table's columns can't reasonably wrap to fix that.
            //
            // type="native" - a plain overflow-x container instead of Mantine's <ScrollArea>.
            // ScrollArea swallows the wheel event over the table and, with its own stylesheet rules
            // missing in this app (see App.css on the Select dropdown and the Drawer), never scrolls
            // itself either: a long list inside a system's Artikelliste could not be scrolled past
            // the fold as long as the cursor sat over the table (see chat). Native overflow keeps
            // the horizontal scrolling this needs and lets vertical wheel events bubble to the page,
            // where they belong.
            <Table.ScrollContainer minWidth={700} type="native">
                <Table className="w-full" styles={TABLE_STYLES} highlightOnHover striped>
                    <Table.Thead>
                        <Table.Tr>
                            {selection && <Table.Th w={40} />}
                            {/* Merkliste first, as in the Dokumenten-Index (see chat:
                                "Merklisten Icon auch am Anfang wie bei den Dokumenten, nicht am
                                Ende") - the badges stay in the trailing column. */}
                            <Table.Th w={40} />
                            <Table.Th w={56} />
                            <Table.Th>
                                <ColumnHeader
                                    filterKey="name"
                                    filterPlaceholder={L.filterPlaceholder}
                                    filterValue={columnFilters.name}
                                    label={L.name}
                                    sort={sort}
                                    sortKey="name"
                                    onFilterChange={onColumnFilter}
                                    onSort={onSort}
                                />
                            </Table.Th>
                            <Table.Th>
                                <ColumnHeader
                                    filterKey="artNr"
                                    filterPlaceholder={L.filterPlaceholder}
                                    filterValue={columnFilters.artNr}
                                    label={L.articleNumber}
                                    sort={sort}
                                    sortKey="artNr"
                                    onFilterChange={onColumnFilter}
                                    onSort={onSort}
                                />
                            </Table.Th>
                            {showCategory && (
                                <Table.Th>
                                    <ColumnHeader
                                        filterKey="category"
                                        filterOptions={filterOptions}
                                        filterPlaceholder={
                                            filterOptions && filterOptions.length > 0
                                                ? L.filterSelectPlaceholder
                                                : L.filterPlaceholder
                                        }
                                        filterValue={columnFilters.category}
                                        label={showLeafOnly ? L.system : L.category}
                                        sort={sort}
                                        sortKey="category"
                                        onFilterChange={onColumnFilter}
                                        onSort={onSort}
                                    />
                                </Table.Th>
                            )}
                            {columns.map((column) => (
                                <Table.Th key={column.key}>
                                    <ColumnHeader
                                        filterKey={column.key}
                                        filterPlaceholder={L.filterPlaceholder}
                                        filterValue={columnFilters[column.key]}
                                        label={column.unit ? `${column.label} (${column.unit})` : column.label}
                                        onFilterChange={onColumnFilter}
                                    />
                                </Table.Th>
                            ))}
                            <Table.Th />
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {items.map((item) => (
                            <Table.Tr
                                key={item.artNr}
                                className="cursor-pointer"
                                tabIndex={0}
                                onClick={() => navigate(openHref(item.artNr))}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        navigate(openHref(item.artNr));
                                    }
                                }}
                            >
                                {selection && (
                                    <Table.Td onClick={(event) => event.stopPropagation()}>
                                        <Checkbox
                                            aria-label={item.name || item.artNr}
                                            checked={selection.selectedArtNrs.has(item.artNr)}
                                            disabled={
                                                !selection.selectedArtNrs.has(item.artNr) &&
                                                selection.selectedArtNrs.size >= selection.max
                                            }
                                            onChange={() => selection.onToggle(item.artNr)}
                                        />
                                    </Table.Td>
                                )}
                                <Table.Td onClick={(event) => event.stopPropagation()}>
                                    <WishlistButton id={item.artNr} size={16} type="product" />
                                </Table.Td>
                                <Table.Td>
                                    {/* Explicit width=height: without it the box defaults to
                                        width:'100%' and stretches across the whole cell, so the
                                        thumbnails still sat at a different x per row despite the
                                        fixed height (see design review, point 3). */}
                                    <ProductImage
                                        alt={item.name || item.artNr}
                                        fallbackSrc={item.image}
                                        height={40}
                                        src={item.imageThumbnailUrl}
                                        width={40}
                                    />
                                </Table.Td>
                                <Table.Td>
                                    {item.name || (
                                        <Text c="dimmed" component="span" fs="italic">
                                            {L.noName}
                                        </Text>
                                    )}
                                </Table.Td>
                                <Table.Td>{item.artNr}</Table.Td>
                                {showCategory && <Table.Td>{categoryText(item.artNr) || '—'}</Table.Td>}
                                {columns.map((column) => (
                                    <Table.Td key={column.key}>{columnValue(item, column.key) || '–'}</Table.Td>
                                ))}
                                <Table.Td>
                                    <Group gap={4} justify="flex-end" wrap="nowrap">
                                        <ScopeBadge item={item} />
                                        <TypeBadge item={item} />
                                    </Group>
                                </Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
            </Table.ScrollContainer>
        );
    }

    return (
        // justify-items-stretch/w="100%" rather than relying on the grid default: the cards came
        // out AS WIDE AS THEIR OWN CONTENT (a tile with a long product name far wider than one
        // next to it, see chat + screenshot "Die Kachelansicht bei den Artikeln skaliert mit
        // unterschiedlichen Breiten"), i.e. something below @uag/doka-design's own base layer
        // suppresses the stretch a grid item normally gets - the same reason the explicit h="100%"
        // below has always been needed for equal row heights. Stating both sizes on the card makes
        // the tiles uniform no matter what the design system does with place-items.
        <div className="grid grid-cols-1 justify-items-stretch gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {items.map((item) => (
                <Card
                    key={item.artNr}
                    className="cursor-pointer transition-shadow hover:shadow-md"
                    h="100%"
                    padding="md"
                    radius="md"
                    role="button"
                    shadow="xs"
                    styles={{ root: { backgroundColor: '#fff' } }}
                    tabIndex={0}
                    w="100%"
                    withBorder
                    onClick={() => navigate(openHref(item.artNr))}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            navigate(openHref(item.artNr));
                        }
                    }}
                >
                    <Stack gap="xs" h="100%">
                        <ProductImage
                            alt={item.name || item.artNr}
                            fallbackSrc={item.image}
                            height={130}
                            src={item.imageThumbnailUrl}
                        />
                        <Group align="flex-start" justify="space-between" wrap="nowrap">
                            {/* minWidth:0 lets the name actually shrink to lineClamp's 2 lines
                            inside this nowrap Group instead of forcing the card (and grid row)
                            wider than the viewport on mobile - same overflow class as
                            SystemDetailContent's/ArticleDetailPage's title header. */}
                            <Text c={brand.primary} fw={600} lineClamp={2} style={{ minWidth: 0 }}>
                                {item.name || (
                                    <Text c="dimmed" component="span" fs="italic" fw={400}>
                                        {L.noName}
                                    </Text>
                                )}
                            </Text>
                            <Group gap={4} wrap="nowrap">
                                <ScopeBadge item={item} />
                                <TypeBadge item={item} />
                                <WishlistButton id={item.artNr} type="product" />
                            </Group>
                        </Group>
                        <Text c="dimmed" size="sm">
                            {item.artNr}
                        </Text>
                        {showCategory && categoryText(item.artNr) && (
                            <Text c="dimmed" lineClamp={1} mt="auto" size="xs">
                                {categoryText(item.artNr)}
                            </Text>
                        )}
                    </Stack>
                </Card>
            ))}
        </div>
    );
};
